import { TFunction } from 'i18next';
import { twMerge } from 'tailwind-merge';
import { KpiObjectType } from '@/api/entities/kpi';
import Card from '@/components/Card';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { NotFound } from '@/components/Message/NotFound';
import { PerClassBarRow, KpiModel } from '@/type/kpi-dashboard.types';
import {
  buildPerClassBarMetrics,
  formatIouScore,
  getKpiObjectLabels,
  hasAnnotations,
  resolveIouTier,
  KPI_TIER_COLORS,
} from '@/utils/kpi-dashboard.utils';
import { PerClassBarChart } from './PerClassBarChart';

interface PerClassIouPanelProps {
  t?: TFunction<undefined>;
  type: KpiObjectType;
  model?: KpiModel;
}

const IOU_AXIS_MAX = 1;
const IOU_AXIS_TICKS = [0, 0.25, 0.5, 0.75, 1];

export const PerClassIouPanel = ({ t, type, model }: PerClassIouPanelProps) => {
  const { object } = getKpiObjectLabels(t, type);

  // `classes` is already ordered by ascending IoU.
  const rows: PerClassBarRow[] = (model?.classes ?? [])
    .filter(hasAnnotations)
    .map((kpiClass) => ({
      id: kpiClass?.id,
      label: kpiClass?.name,
      value: kpiClass?.iou,
      color: KPI_TIER_COLORS[resolveIouTier(kpiClass?.iou)].solid,
      metrics: buildPerClassBarMetrics(t, kpiClass),
    }));

  return (
    <Card
      title={
        <TitleSection
          text={t('perObjectIou', { object })}
          defaultIconClassName="ki-chart-line-up-2"
        />
      }
      bodyClassName={twMerge('pt-5 pb-8 flex flex-col gap-5', !model && 'pt-8')}
    >
      {!model ? (
        <NotFound text={`${t('common:noDataFound')}...`} />
      ) : (
        <>
          <p className="text-sm font-medium text-slate-600">
            {t('sortedAscendingWorstFirst')}
          </p>
          <PerClassBarChart
            rows={rows}
            axisMax={IOU_AXIS_MAX}
            ticks={IOU_AXIS_TICKS}
            formatValue={formatIouScore}
          />
        </>
      )}
    </Card>
  );
};
